import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import { mediaQuery } from 'lib/styledSystem';
import { Container, Box, Hide } from '@qga/roo-ui/components';
import { useDispatch, useSelector } from 'react-redux';
import LocationAutocompleter from 'components/LocationAutocompleter';
import OccupantPicker from 'components/OccupantPicker';
import AvailabilityDatePicker from 'components/AvailabilityDatePicker';
import { getQueryLocation, getQueryCheckIn, getQueryCheckOut, getQueryOccupants } from 'store/router/routerSelectors';
import { routeToProperty, updateQuery } from 'store/search/searchActions';
import { Row, Column } from './primitives';
import FixedSearchControls from 'components/FixedSearchControls';
import { FieldError } from '@qga/components';
import { useEndDate } from './../../../hooks/useEndDate';

const columnGaps = [12];
const labelOptions = { color: 'greys.alto' };

const InputErrorField = styled(FieldError)`
  margin-top: ${themeGet('space.2')};
`;

const SearchControlsWrapper = styled(Box)`
  z-index: ${themeGet('zIndices.searchControls')};
  position: relative;
  ${mediaQuery.minWidth.md} {
    z-index: inherit;
  }
`;

const ConnectedOccupantPicker = ({ onUpdateQuery }) => {
  const occupants = useSelector(getQueryOccupants);

  return (
    <OccupantPicker
      occupants={occupants}
      labelOptions={labelOptions}
      updateQuery={onUpdateQuery}
      viewThreshold={0}
      verboseInMobile={false}
    />
  );
};

ConnectedOccupantPicker.propTypes = {
  onUpdateQuery: PropTypes.func.isRequired,
};

const ConnectedLocationAutocompleter = ({ onUpdateQuery }) => {
  const location = useSelector(getQueryLocation);
  const dispatch = useDispatch();
  const onRouteToProperty = useCallback((payload) => dispatch(routeToProperty(payload)), [dispatch]);

  return (
    <LocationAutocompleter
      locationName={location}
      labelOptions={labelOptions}
      updateQuery={onUpdateQuery}
      routeToProperty={onRouteToProperty}
    />
  );
};

ConnectedLocationAutocompleter.propTypes = {
  onUpdateQuery: PropTypes.func.isRequired,
};

const ConnectedDatePicker = ({ onUpdateQuery, hasEndDate }) => {
  const checkIn = useSelector(getQueryCheckIn);
  const checkOut = useSelector(getQueryCheckOut);
  const selectedDates = useMemo(() => ({ startDate: checkIn, endDate: checkOut }), [checkIn, checkOut]);
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const smallTablet = windowWidth > 767 && windowWidth < 920;
  return (
    <AvailabilityDatePicker
      selectedDates={selectedDates}
      labelOptions={labelOptions}
      backgroundColor={'white'}
      updateQuery={onUpdateQuery}
      hasEndDate={hasEndDate}
      anchorX={smallTablet ? 'center' : 'right'}
      isLimited={true}
    />
  );
};

ConnectedDatePicker.propTypes = {
  onUpdateQuery: PropTypes.func.isRequired,
  hasEndDate: PropTypes.func,
};

const SearchControls = () => {
  const dispatch = useDispatch();
  const onUpdateQuery = useCallback((payload) => dispatch(updateQuery(payload)), [dispatch]);
  const checkOut = useSelector(getQueryCheckOut);
  const { checkoutDate, setCheckoutDate } = useEndDate();
  const hasEndDate = (endDate) => {
    setCheckoutDate(endDate);
  };

  return (
    <SearchControlsWrapper bg="white" py={5}>
      <Container>
        <Row gap={columnGaps}>
          <Column gap={columnGaps} cols={[12, 4, 6]} mb={[3, 0]}>
            <ConnectedLocationAutocompleter onUpdateQuery={onUpdateQuery} />
          </Column>

          <Column gap={columnGaps} cols={[7, 6, 4]} data-testid="stay-date-picker">
            {checkOut !== null && <ConnectedDatePicker onUpdateQuery={onUpdateQuery} hasEndDate={hasEndDate} />}
            {checkoutDate === null && (
              <Box flexBasis={['93%', '97%', '400px']}>
                <InputErrorField error={{ message: 'Select check-out date' }} data-testid="missing-checkOut-error" />
              </Box>
            )}
          </Column>

          <Column gap={columnGaps} cols={[5, 2, 2]}>
            <ConnectedOccupantPicker onUpdateQuery={onUpdateQuery} />
          </Column>

          <Hide md lg>
            <FixedSearchControls isListSearch />
          </Hide>
        </Row>
      </Container>
    </SearchControlsWrapper>
  );
};

export default SearchControls;
